import React, { createContext, useContext, useRef, useCallback, useEffect } from "react";

// Silent refresh — bump() calls every registered refetch callback so pages
// re-pull their data IN PLACE. No React key remount, so NO screen blink / blank
// flash / scroll reset. Pull-to-refresh and auto-refresh now feel native.
const RefreshContext = createContext({ bump: () => {}, subscribe: () => () => {} });

export function RefreshProvider({ children }) {
  const subs = useRef(new Set());

  const subscribe = useCallback((fn) => {
    subs.current.add(fn);
    return () => subs.current.delete(fn);
  }, []);

  const bump = useCallback(() => {
    subs.current.forEach((fn) => {
      try { fn(); } catch (_) {}
    });
  }, []);

  return (
    <RefreshContext.Provider value={{ bump, subscribe }}>
      {children}
    </RefreshContext.Provider>
  );
}

export const useRefresh = () => useContext(RefreshContext);

// Register a silent refetch callback that runs on every bump()
// (pull-to-refresh / auto-refresh) WITHOUT remounting the page.
export function useRefreshSignal(fn) {
  const { subscribe } = useRefresh();
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => subscribe(() => ref.current && ref.current()), [subscribe]);
}
